import React, { useState, useEffect } from 'react';
import Header from '@/components/ui/Header';
import { useNavigate } from 'react-router-dom';
import ProgressIndicator from './components/ProgressIndicator';
import SkillsInventory from './components/SkillsInventory';
import CareerGoals from './components/CareerGoals';
import WorkExperience from './components/WorkExperience';
import WorkPreferences from './components/WorkPreferences';
import AssessmentComplete from './components/AssessmentComplete';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';

const CareerAssessment = () => {
const navigate = useNavigate();
const [currentStep, setCurrentStep] = useState(1);
const [assessmentData, setAssessmentData] = useState({});
const [isComplete, setIsComplete] = useState(false);
const [savedProgress, setSavedProgress] = useState(false);

const totalSteps = 4;
const completedSteps = currentStep - 1;
const estimatedTimes = {
1: 8, // Skills Inventory
2: 5, // Career Goals
3: 10, // Work Experience
4: 6  // Work Preferences
};

// Load saved progress on component mount
useEffect(() => {
const savedData = localStorage.getItem('career-assessment-progress');
if (savedData) {
try {
const parsed = JSON.parse(savedData);
setAssessmentData(parsed?.data || {});
setCurrentStep(parsed?.currentStep || 1);
setSavedProgress(true);
} catch (error) {
console.error('Error loading saved progress:', error);
}
}
}, []);

// Save progress to localStorage
const saveProgress = (stepData) => {
const updatedData = { ...assessmentData, ...stepData };
setAssessmentData(updatedData);

const progressData = {
data: updatedData,
currentStep: currentStep,
lastSaved: new Date()?.toISOString()
};

localStorage.setItem('career-assessment-progress', JSON.stringify(progressData));
setSavedProgress(true);
};

const handleNext = () => {
if (currentStep < totalSteps) {
setCurrentStep(currentStep + 1);
} else {
setIsComplete(true);
// Clear saved progress when assessment is complete
localStorage.removeItem('career-assessment-progress');
}
};

const handlePrevious = () => {
if (currentStep > 1) {
setCurrentStep(currentStep - 1);
}
};

const handleRestart = () => {
setCurrentStep(1);
setAssessmentData({});
setIsComplete(false);
setSavedProgress(false);
localStorage.removeItem('career-assessment-progress');
};

const clearProgress = () => {
localStorage.removeItem('career-assessment-progress');
setSavedProgress(false);
handleRestart();
};

const renderCurrentStep = () => {
if (isComplete) {
return (
<AssessmentComplete 
assessmentData={assessmentData}
onRestart={handleRestart}
/>
);
}

switch (currentStep) {
case 1:
return (
<SkillsInventory
onNext={handleNext}
onSave={saveProgress}
savedData={assessmentData}
/>
);
case 2:
return (
<CareerGoals
onNext={handleNext}
onPrevious={handlePrevious}
onSave={saveProgress}
savedData={assessmentData}
/>
);
case 3:
return (
<WorkExperience
onNext={handleNext}
onPrevious={handlePrevious}
onSave={saveProgress}
savedData={assessmentData}
/>
);
case 4:
return (
<WorkPreferences
onNext={handleNext}
onPrevious={handlePrevious}
onSave={saveProgress}
savedData={assessmentData}
/>
);
default:
return null;
}
};

return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-4xl mx-auto px-6 py-8">
{/* Page Header */}
<div className="mb-8">
<div className="flex items-center space-x-3 mb-4">
<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
<Icon name="ClipboardList" size={24} className="text-primary" />
</div>
<div>
<h1 className="text-3xl font-bold text-text-primary">Career Assessment</h1>
<p className="text-text-secondary">
{isComplete 
? 'Assessment completed successfully!'
: 'Discover your ideal career path through comprehensive evaluation'
}
</p>
</div>
</div>

{/* Saved Progress Indicator */}
{savedProgress && !isComplete && (
<div className="flex items-center justify-between p-4 bg-success/10 border border-success/20 rounded-lg">
<div className="flex items-center space-x-3">
<Icon name="Save" size={20} className="text-success" />
<div>
<p className="text-sm font-medium text-text-primary">Progress Saved</p>
<p className="text-xs text-text-secondary">You can continue where you left off</p>
</div>
</div>
<Button
variant="ghost"
size="sm"
onClick={clearProgress}
iconName="RotateCcw"
>
Start Over
</Button>
</div>
)}
</div>

{/* Progress Indicator */}
{!isComplete && (
<ProgressIndicator
currentStep={currentStep}
totalSteps={totalSteps}
completedSteps={completedSteps}
estimatedTime={estimatedTimes?.[currentStep] || 5}
/>
)}

{/* Assessment Content */}
<div className="mb-8">
{renderCurrentStep()}
</div>

{/* Help Section */}
{!isComplete && (
<div className="bg-muted border border-border rounded-lg p-6">
<div className="flex items-start space-x-3">
<Icon name="HelpCircle" size={20} className="text-primary flex-shrink-0 mt-0.5" />
<div>
<h4 className="font-medium text-text-primary mb-2">Need Help?</h4>
<div className="text-sm text-text-secondary space-y-2">
<p>• Your progress is automatically saved as you complete each section</p>
<p>• You can return to previous sections to update your responses</p>
<p>• Take your time - there's no rush to complete the assessment</p>
<p>• For technical support, contact our help team</p>
</div>
<div className="mt-4">
<Button
variant="outline"
size="sm"
iconName="MessageCircle"
onClick={() => navigate('/support')}
>
Contact Support
</Button>
</div>
</div>
</div>
</div>
)}
</div>
</main>
</div>
);
};

export default CareerAssessment;